/**
 * Einstellungen → Profil → Konto löschen.
 *
 * Apple 5.1.1(v) und die Play-Richtlinie zur Kontolöschung verlangen beide einen
 * Weg, das Konto in der App selbst zu löschen — deshalb sitzt der Abschnitt am
 * Ende der Profilseite, wo Patienten (und Prüfer) ihn suchen.
 *
 * Das Backend löscht endgültig: DELETE /:locale/v2/:token/patient.json
 * (V2::PatientsController#destroy).
 */
import React, { useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import http from "@shared/api/http";
import { unsubscribe } from "@shared/push/pushService";
import { disableNativePush } from "@shared/push/nativePush";
import "./Preferences.css";

export default function DeleteAccount() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const close = () => {
    if (busy) return;
    setShowModal(false);
    setUnderstood(false);
    setError(false);
  };

  const deleteAccount = async () => {
    setBusy(true);
    setError(false);
    try {
      // Das Gerät zuerst abmelden: nach der Löschung authentifiziert der Token
      // den Aufruf nicht mehr. Schlägt es fehl, ist das kein Grund abzubrechen —
      // der Server räumt die Subscriptions ohnehin mit dem Patienten ab.
      try {
        await unsubscribe();
        await disableNativePush();
      } catch (pushError) {
        /* best effort */
      }
      await http.delete("/:locale/v2/:token/patient.json");
      localStorage.removeItem("access_token");
      localStorage.removeItem("userData");
      window.location.assign("/");
    } catch (requestError) {
      setError(true);
      setBusy(false);
    }
  };

  return (
    <>
      <Row className="h-100 table-row delete-account">
        <Col md={3} className="table-column-title">
          {t("account.delete.title")}
        </Col>
        <Col md={7}>
          <p className="delete-account-hint">{t("account.delete.intro")}</p>
          <Button
            variant="outline-danger"
            className="delete-account-button"
            onClick={() => setShowModal(true)}
          >
            {t("account.delete.button")}
          </Button>
        </Col>
      </Row>

      <Modal show={showModal} onHide={close} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>{t("account.delete.modal_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("account.delete.modal_body")}</p>
          <ul className="delete-account-list">
            <li>{t("account.delete.item_profile")}</li>
            <li>{t("account.delete.item_programs")}</li>
            <li>{t("account.delete.item_reminders")}</li>
          </ul>
          <p className="delete-account-hint">{t("account.delete.therapist_note")}</p>
          <p className="delete-account-warning">{t("account.delete.irreversible")}</p>
          <Form.Check
            type="checkbox"
            id="delete-account-confirm"
            label={t("account.delete.confirm_checkbox")}
            checked={understood}
            disabled={busy}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          {error && (
            <Alert variant="danger" className="delete-account-error">
              {t("account.delete.error")}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {t("actions.cancel")}
          </Button>
          <Button variant="danger" onClick={deleteAccount} disabled={!understood || busy}>
            {busy ? t("account.delete.deleting") : t("account.delete.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
